import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LocalStorageKey } from '@/lib/constants';

export enum SideDrawerContextType {
  Task = 'task',
  OttoPilot = 'otto-pilot',
  Conversation = 'conversation',
  ConversationHistory = 'conversation-history',
}

interface SideDrawerContextTaskData {
  taskId: string;
}

interface SideDrawerContextConversationData {
  conversationId: string;
}

type SideDrawerTaskContext = {
  type: SideDrawerContextType.Task;
  data: SideDrawerContextTaskData;
};

type SideDrawerOttoPilotContext = {
  type: SideDrawerContextType.OttoPilot;
};

type SideDrawerConversationContext = {
  type: SideDrawerContextType.Conversation;
  data: SideDrawerContextConversationData;
};

type SideDrawerConversationHistoryContext = {
  type: SideDrawerContextType.ConversationHistory;
};

type SideDrawerContext =
  | SideDrawerTaskContext
  | SideDrawerOttoPilotContext
  | SideDrawerConversationContext
  | SideDrawerConversationHistoryContext;

const defaultContext: SideDrawerContext = {
  type: SideDrawerContextType.OttoPilot,
};

type ConversationData = {
  conversation: {
    aiSummary: string | null;
    currentAgent: {
      name: string;
    } | null;
  };
} | null;

interface SideDrawerState {
  isOpen: boolean;
  context: SideDrawerContext;
  conversationData: ConversationData;
  openDrawer: (context: SideDrawerContext) => void;
  closeDrawer: () => void;
  toggleDrawer: (context?: SideDrawerContext) => void;
  resetDrawer: () => void;
  updateConversationData: (data: ConversationData) => void;
}

export const useSideDrawer = create<SideDrawerState>()(
  persist(
    (set) => ({
      isOpen: false,
      context: defaultContext,
      conversationData: null,
      openDrawer: (context: SideDrawerContext) => set({ isOpen: true, context }),
      closeDrawer: () => set({ isOpen: false }),
      toggleDrawer: (context?: SideDrawerContext) => {
        set((state) => {
          return {
            isOpen: isEqual(context, state.context) ? !state.isOpen : true,
            context: context ?? undefined,
          };
        });
      },
      resetDrawer: () => set({ isOpen: false, context: defaultContext }),
      updateConversationData: (data: ConversationData) => set({ conversationData: data }),
    }),
    {
      name: LocalStorageKey.SIDE_DRAWER_STORE,
      // Version 0 could persist the removed appointment context, which no view renders.
      version: 1,
      migrate: () => ({ isOpen: false, context: defaultContext }),
      partialize: (state) => ({
        isOpen: state.isOpen,
        context: state.context,
        // Don't persist conversationData as it can be stale
      }),
    }
  )
);
